import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api.js";
import { motion, AnimatePresence } from "framer-motion";

export default function Methods({ onSubmit = () => {} }) {
  const navigate = useNavigate();
  const [methods, setMethods] = useState([]);
  const [categories, setCategories] = useState({});
  const [presets, setPresets] = useState({});
  const [presetOrder, setPresetOrder] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Per-strategy config (e.g. crescendo). `strategyConfig` is the schema served by the
  // backend; `customStrategies` holds the user's saved values keyed by strategy id:
  //   { crescendo: { config: { maxTurns, maxBacktracks, stateful } } }
  const [strategyConfig, setStrategyConfig] = useState({});
  const [customStrategies, setCustomStrategies] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("customStrategies") || "{}");
    } catch {
      return {};
    }
  });
  const [pendingConfig, setPendingConfig] = useState([]);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [currentStrategy, setCurrentStrategy] = useState(null);
  const [modalDraft, setModalDraft] = useState({});
  const dialogRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const response = await API.get(
          `${process.env.REACT_APP_DJANGO_API_URL}/methods/`
        );
        const data = response.data.data;

        setCategories(data.categories);
        setPresets(data.presets);
        setPresetOrder(Object.keys(data.presets));
        setStrategyConfig(data.strategy_config || {});
        // Persist the schema so the Review step can label per-strategy config with the
        // user-facing field labels (e.g. "Streaming") instead of raw keys ("stateful").
        sessionStorage.setItem(
          "strategyConfigSchema",
          JSON.stringify(data.strategy_config || {})
        );

        const categoryMap = {};
        for (const category in data.categories) {
          for (const methodId of data.categories[category]) {
            if (!categoryMap[methodId]) categoryMap[methodId] = [];
            categoryMap[methodId].push(category);
          }
        }

        const presetMap = {};
        for (const preset in data.presets) {
          for (const methodId of data.presets[preset]) {
            if (!presetMap[methodId]) presetMap[methodId] = [];
            presetMap[methodId].push(preset);
          }
        }

        const methodList = Object.keys(data.methods_names).map((id) => ({
          id,
          name: data.methods_names[id],
          description:
            data.methods_descriptions[id] || "No description available.",
          categories: categoryMap[id] || [],
          presets: presetMap[id] || [],
        }));

        setMethods(methodList);
      } catch (err) {
        setError("Failed to fetch method data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();

    const savedData = sessionStorage.getItem("selectedMethods");
    if (savedData) setSelected(new Set(JSON.parse(savedData)));
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      "selectedMethods",
      JSON.stringify(Array.from(selected))
    );
  }, [selected]);

  useEffect(() => {
    sessionStorage.setItem("customStrategies", JSON.stringify(customStrategies));
  }, [customStrategies]);

  // Build the default config for a schema-backed strategy.
  const defaultsFor = (sid) => {
    const out = {};
    (strategyConfig[sid]?.fields || []).forEach((f) => {
      out[f.key] = f.default;
    });
    return out;
  };

  // A field is disabled when its `disabledWhen` rule is satisfied by the draft.
  const isFieldDisabled = (field) => {
    const dw = field.disabledWhen;
    return dw ? modalDraft[dw.field] === dw.equals : false;
  };

  // Update one field in the draft and re-apply any disabledWhen pins.
  const setField = (field, value) => {
    setModalDraft((prev) => {
      const next = { ...prev, [field.key]: value };
      (strategyConfig[currentStrategy]?.fields || []).forEach((f) => {
        const dw = f.disabledWhen;
        if (dw && next[dw.field] === dw.equals) next[f.key] = f.min ?? 0;
      });
      return next;
    });
  };

  const openConfig = (sid) => {
    openerRef.current = document.activeElement;
    setCurrentStrategy(sid);
    setModalDraft({ ...defaultsFor(sid), ...(customStrategies[sid]?.config || {}) });
    setIsConfigOpen(true);
  };

  const handleConfigSave = () => {
    if (!currentStrategy) return;
    const fields = strategyConfig[currentStrategy]?.fields || [];
    const cfg = {};
    fields.forEach((f) => {
      let v = modalDraft[f.key];
      if (f.type === "int") {
        v = parseInt(v, 10);
        if (Number.isNaN(v)) v = f.default;
        if (f.min != null) v = Math.max(f.min, v);
        if (f.max != null) v = Math.min(f.max, v);
      } else if (f.type === "bool") {
        v = Boolean(v);
      }
      cfg[f.key] = v;
    });
    // Re-apply disabledWhen pins (e.g. streaming on -> backtracks 0).
    fields.forEach((f) => {
      const dw = f.disabledWhen;
      if (dw && cfg[dw.field] === dw.equals) cfg[f.key] = f.min ?? 0;
    });
    setCustomStrategies((prev) => ({ ...prev, [currentStrategy]: { config: cfg } }));
    setIsConfigOpen(false);
    setCurrentStrategy(null);
  };

  const handleConfigCancel = () => {
    // Keep the strategy selected; backend applies schema defaults when no config is stored.
    setIsConfigOpen(false);
    setCurrentStrategy(null);
  };

  const toggleSelection = (methodId) => {
    const updated = new Set(selected);
    if (updated.has(methodId)) {
      updated.delete(methodId);
      if (customStrategies[methodId]) {
        setCustomStrategies((prev) => {
          const next = { ...prev };
          delete next[methodId];
          return next;
        });
      }
    } else {
      updated.add(methodId);
      // Strategies with a config schema open a popup so the user can tune them.
      // Skip if already configured (re-open via the ⚙ button) and de-dupe the queue.
      if (strategyConfig[methodId] && !customStrategies[methodId]) {
        setPendingConfig((prev) =>
          prev.includes(methodId) ? prev : [...prev, methodId]
        );
      }
    }
    setSelected(updated);
  };

  // Open the config popup for the next pending strategy (one at a time).
  useEffect(() => {
    if (!isConfigOpen && pendingConfig.length > 0) {
      const sid = pendingConfig[0];
      openerRef.current = document.activeElement;
      setCurrentStrategy(sid);
      setModalDraft({ ...defaultsFor(sid), ...(customStrategies[sid]?.config || {}) });
      setIsConfigOpen(true);
      setPendingConfig((prev) => prev.slice(1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigOpen, pendingConfig]);

  // Modal a11y: ESC to cancel, autofocus first control on open, return focus on close.
  useEffect(() => {
    if (!isConfigOpen) {
      openerRef.current?.focus?.();
      return;
    }
    const onKey = (e) => {
      if (e.key === "Escape") handleConfigCancel();
    };
    document.addEventListener("keydown", onKey);
    dialogRef.current
      ?.querySelector("input:not([disabled]), button")
      ?.focus();
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigOpen]);

  const isPresetActive = (preset) => {
    const methodIds = presets[preset] || [];
    return methodIds.every((id) => selected.has(id));
  };

  const handlePresetSelect = (preset) => {
    const methodIds = presets[preset] || [];
    const updated = new Set(selected);
    const currentlyActive = isPresetActive(preset);

    methodIds.forEach((id) => {
      if (currentlyActive) {
        updated.delete(id);
      } else {
        updated.add(id);
      }
    });
    setSelected(updated);
  };

  const selectAllMethods = () => {
    const allIds = methods.map((d) => d.id);
    setSelected(new Set(allIds));
  };

  const deselectAllMethods = () => {
    setSelected(new Set());
  };

  const selectCategory = (category) => {
    const updated = new Set(selected);
    categories[category].forEach((id) => updated.add(id));
    setSelected(updated);
  };

  const deselectCategory = (category) => {
    const updated = new Set(selected);
    categories[category].forEach((id) => updated.delete(id));
    setSelected(updated);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (selected.size === 0) {
      alert("Select at least one method to continue.");
      return;
    }

    const selectedMethods = Array.from(selected);

    try {
      await API.post(
        `${process.env.REACT_APP_DJANGO_API_URL}/methods/`,
        { methods: selectedMethods },
        { headers: { "Content-Type": "application/json" } }
      );

      onSubmit(selectedMethods);
      navigate("/newscan/review");
    } catch (err) {
      console.error("Submission error:", err);
      alert("Failed to save methods. Check console for details.");
    }
  };

  if (loading)
    return (
      <div className="p-6 text-white animate-pulse">Loading methods...</div>
    );
  if (error) return <div className="p-6 text-red-500">{error}</div>;

  const filteredMethods = methods.filter(
    (method) =>
      method.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      method.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredCategories = Object.keys(categories).reduce((acc, category) => {
    const categoryMethods = categories[category].filter((methodId) =>
      filteredMethods.some((d) => d.id === methodId)
    );
    if (categoryMethods.length > 0) {
      acc[category] = categoryMethods;
    }
    return acc;
  }, {});

  return (
    <form onSubmit={handleFormSubmit} className="text-white bg-transparent flex flex-col w-auto">
      <div className="mx-6">
        <div className="flex items-center justify-between mt-6 mb-4">
          <h2 className="text-2xl font-bold">Method Configuration</h2>
          <div className="w-full max-w-xs">
            <input
              type="text"
              placeholder="Search methods..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-[#23242a] text-white border border-gray-700 focus:outline-none focus:border-purple-400"
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={selectAllMethods}
              className="px-4 py-2 rounded-lg text-sm font-medium border bg-[#23242a] text-green-200 border-green-500 hover:border-green-400 hover:bg-green-700/40"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={deselectAllMethods}
              className="px-4 py-2 rounded-lg text-sm font-medium border bg-[#23242a] text-red-200 border-red-500 hover:border-red-400 hover:bg-red-700/40"
            >
              Select None
            </button>
          </div>
        </div>

        <div className="mb-6 flex flex-wrap gap-3">
          {presetOrder.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => handlePresetSelect(preset)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-200
                ${isPresetActive(preset)
                  ? "bg-purple-700/70 text-white border-purple-400 shadow-md"
                  : "bg-[#23242a] text-purple-200 border-[#43314c] hover:border-purple-400"}`}
            >
              {preset}
            </button>
          ))}
        </div>
      </div>

      <div className="w-full px-6 pb-4">
        <div
          className={`grid gap-8 justify-start items-start ${
            Object.keys(filteredCategories).length === 1
              ? "grid-cols-1"
              : Object.keys(filteredCategories).length === 2
              ? "grid-cols-1 md:grid-cols-2"
              : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {Object.keys(filteredCategories).map((category) => (
            <div
              key={category}
              className="bg-[#181a20] border border-gray-800 rounded-xl shadow-lg overflow-hidden flex flex-col"
            >
              <div className="px-6 py-4 border-b border-gray-800 h-[7.5vh] bg-[#22232a] sticky top-0 z-10 flex justify-between items-center">
                <h3 className="font-bold text-base tracking-wide uppercase text-green-400">
                  {category.replace("_", " ")}
                </h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => selectCategory(category)}
                    className="text-sm px-2 py-1 rounded-lg font-medium border bg-[#23242a] text-green-200 border-green-500 hover:border-green-400 hover:bg-green-700/40"
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => deselectCategory(category)}
                    className="text-sm px-2 py-1 rounded-lg font-medium border bg-[#23242a] text-red-200 border-red-500 hover:border-red-400 hover:bg-red-700/40"
                  >
                    None
                  </button>
                </div>
              </div>

              <div className="flex-1 p-4 space-y-5 overflow-y-auto min-h-[150px] max-h-[70vh]">
                <AnimatePresence>
                  {filteredCategories[category].map((methodId) => {
                    const method = methods.find((d) => d.id === methodId);
                    if (!method) return null;

                    return (
                      <motion.div
                        key={method.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.28 }}
                        onClick={() => toggleSelection(method.id)}
                        className={`p-5 rounded-xl border relative cursor-pointer group
                        transition-all duration-300 shadow-sm ${
                          selected.has(method.id)
                            ? "border-blue-400 bg-blue-800/30 shadow-lg"
                            : "border-[#232325] bg-[#23242a] hover:border-blue-400/60"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-base truncate">
                            {method.name}
                          </h4>
                          <input
                            type="checkbox"
                            checked={selected.has(method.id)}
                            onChange={() => toggleSelection(method.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="form-checkbox accent-blue-500 h-4 w-4 rounded"
                          />
                        </div>
                        <p className="text-sm text-gray-300 mt-2 mb-3 line-clamp-3">
                          {method.description}
                        </p>
                        {strategyConfig[method.id] && selected.has(method.id) && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openConfig(method.id);
                            }}
                            className="text-xs text-blue-300 border border-blue-400/50 rounded-lg px-2 py-1 hover:bg-blue-500/20 transition"
                          >
                            ⚙ Configure
                            {customStrategies[method.id]?.config && (
                              <span className="text-gray-400 ml-1">
                                (
                                {Object.entries(customStrategies[method.id].config)
                                  .map(([k, v]) => {
                                    const f = (strategyConfig[method.id]?.fields || []).find(
                                      (x) => x.key === k
                                    );
                                    const val =
                                      typeof v === "boolean" ? (v ? "on" : "off") : v;
                                    return `${f?.label || k}: ${val}`;
                                  })
                                  .join(", ")}
                                )
                              </span>
                            )}
                          </button>
                        )}
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between mt-7 px-6 mb-8">
        <button
          type="button"
          onClick={() => navigate("/newscan/detectors")}
          className="bg-gray-700/40 text-gray-200 border border-gray-600 px-6 py-2 rounded-xl hover:bg-gray-600/40 transition"
        >
          Back
        </button>
        <button
          type="submit"
          className="px-6 py-2 rounded-xl transition border bg-blue-600/30 text-blue-300 border-blue-400 hover:bg-blue-500/40"
        >
          Next
        </button>
      </div>

      {isConfigOpen && currentStrategy && strategyConfig[currentStrategy] && (
        <div
          onClick={handleConfigCancel}
          className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50"
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="strategy-cfg-title"
            aria-describedby="strategy-cfg-desc"
            onClick={(e) => e.stopPropagation()}
            className="bg-[#181a20] rounded-lg p-8 max-w-md w-full border border-gray-700"
          >
            <h2 id="strategy-cfg-title" className="text-xl font-bold mb-1">
              {strategyConfig[currentStrategy].label || currentStrategy} settings
            </h2>
            <p id="strategy-cfg-desc" className="text-sm text-gray-400 mb-5">
              Tune this multi-turn strategy. These settings are saved with the
              configuration and restored on re-run.
            </p>
            <div className="space-y-5 max-h-[55vh] overflow-y-auto pr-1">
              {strategyConfig[currentStrategy].fields.map((field) => {
                const disabled = isFieldDisabled(field);
                const fieldId = `cfg-${field.key}`;
                const helpId = `${fieldId}-help`;
                const triggerLabel =
                  field.disabledWhen &&
                  (strategyConfig[currentStrategy].fields.find(
                    (f) => f.key === field.disabledWhen.field
                  )?.label || field.disabledWhen.field);
                return (
                  <div
                    key={field.key}
                    aria-disabled={disabled || undefined}
                    className={disabled ? "opacity-50" : ""}
                  >
                    <div className="flex items-center justify-between">
                      <label htmlFor={fieldId} className="font-medium text-sm">
                        {field.label}
                        {field.type === "int" && (
                          <span className="text-gray-500 ml-1">
                            ({field.min}–{field.max})
                          </span>
                        )}
                      </label>
                      {field.type === "bool" ? (
                        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                          <input
                            id={fieldId}
                            type="checkbox"
                            className="sr-only peer"
                            aria-label={field.label}
                            checked={Boolean(modalDraft[field.key])}
                            onChange={(e) => setField(field, e.target.checked)}
                          />
                          <div className="w-11 h-6 bg-[#232325] rounded-full peer peer-checked:bg-blue-500 peer-focus:ring-2 peer-focus:ring-blue-500 transition-colors duration-300" />
                          <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-300 transform peer-checked:translate-x-5" />
                        </label>
                      ) : (
                        <input
                          id={fieldId}
                          type="number"
                          min={field.min}
                          max={field.max}
                          disabled={disabled}
                          aria-describedby={field.help ? helpId : undefined}
                          value={modalDraft[field.key] ?? ""}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === "") {
                              setField(field, "");
                              return;
                            }
                            let v = parseInt(raw, 10);
                            if (Number.isNaN(v)) return;
                            if (field.min != null) v = Math.max(field.min, v);
                            if (field.max != null) v = Math.min(field.max, v);
                            setField(field, v);
                          }}
                          className="w-20 bg-[#101012] border border-[#232325] rounded-lg px-3 py-1.5 text-white focus:border-blue-400 focus:outline-none disabled:cursor-not-allowed"
                        />
                      )}
                    </div>
                    {field.help && (
                      <p id={helpId} className="text-xs text-gray-500 mt-1">
                        {field.help}
                      </p>
                    )}
                    {disabled && field.disabledWhen && (
                      <p className="text-xs text-amber-300/80 mt-1">
                        Unavailable while {triggerLabel} is on — set to{" "}
                        {field.min ?? 0}.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-4 mt-7">
              <button
                type="button"
                onClick={handleConfigCancel}
                className="bg-gray-700/40 text-gray-200 border border-gray-600 px-6 py-2 rounded-xl hover:bg-gray-600/40 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfigSave}
                className="bg-blue-600/30 text-blue-300 border border-blue-400 px-6 py-2 rounded-xl hover:bg-blue-500/40 transition"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}